import { FilterChipContainer, ToggleFiltersButton, ToggleFiltersToolbar } from '@retail/components';
import { Stack } from '@mui/material';
import { GridApiPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useExportFromBulkEdit } from '@retail/my-assortment/data-access';
import { AssortmentItemMinimal } from '@retail/my-assortment/types';
import {
  BulkEditFilter,
  IconButton,
  SelectedItemsTable,
  TotalItemsLoader,
} from '@retail/products/components';
import { useDisclosure } from '@shared/hooks';
import { MutableRefObject, useCallback } from 'react';
import { ExcelIcon2 } from '@shared/custom-icons';
import { useBulkEditSelectedItemsFilterOptions } from '@retail/products/context';
import { itemBulkActions } from '@retail/products/utils';

interface Props {
  apiRef?: MutableRefObject<GridApiPremium | null>;
  selectedItems: AssortmentItemMinimal[];
  duplicateRowIds: number[];
  rowCount: number;
  setDuplicateRowIds: (ids: number[]) => void;
  setRowCount: (count: number) => void;
  removeItem: (mgSupplierItemId: number) => void;
}

export function BulkEditSelectedItems({
  apiRef: apiRefProps,
  selectedItems,
  duplicateRowIds,
  rowCount,
  setDuplicateRowIds,
  setRowCount,
  removeItem,
}: Props) {
  const { isBrandContext } = useSelectedOrgUnit();

  const internalApiRef = useGridApiRef();
  const apiRef = apiRefProps ?? internalApiRef;
  const { exportAsXLSX } = useExportFromBulkEdit();

  const handleExportAsCsv = useCallback(() => {
    const rowIds = apiRef.current?.getAllRowIds();
    const rows = rowIds?.map((id) => apiRef.current?.getRow(id)) ?? [];
    exportAsXLSX(rows);
  }, [apiRef, exportAsXLSX]);

  const showFilters = useDisclosure(false);

  const { suppliers, productGroupHierarchy, productGroups, discountGroups } =
    useBulkEditSelectedItemsFilterOptions({ selectedItems });

  return (
    <Stack flexGrow={1}>
      <ToggleFiltersToolbar sx={{ mb: 1, justifyContent: 'space-between' }}>
        <FilterChipContainer
          allowedKeys={itemBulkActions}
          filterOptions={{ suppliers, discountGroups, productGroups }}
        />
        <Stack flexDirection="row" alignItems="flex-end" gap={2}>
          <TotalItemsLoader
            totalItems={selectedItems.length}
            itemsShowing={rowCount}
            isFetching={false}
          />

          <IconButton sx={{ p: 0 }} onClick={handleExportAsCsv}>
            <ExcelIcon2 fontSize="small" />
          </IconButton>

          <ToggleFiltersButton
            showFilters={showFilters.isOpen}
            toggleFilters={showFilters.onToggle}
          />
        </Stack>
      </ToggleFiltersToolbar>

      <Stack flexGrow={1} flexDirection="row">
        <SelectedItemsTable
          apiRef={apiRef}
          assortmentItems={selectedItems}
          includeStocked={!isBrandContext}
          duplicateRowIds={duplicateRowIds}
          setRowCount={setRowCount}
          setDuplicateRowIds={setDuplicateRowIds}
          removeItem={removeItem}
        />
        {showFilters.isOpen && (
          <BulkEditFilter
            showFilters={showFilters}
            suppliers={suppliers}
            productGroupHierarchy={productGroupHierarchy}
            discountGroups={discountGroups}
          />
        )}
      </Stack>
    </Stack>
  );
}
